import type { ModuleLayout, StudioSystemType } from 'types/studio/items'
import { parseIntegrationJson } from 'util/misc'
import {
  getSeamClampTypesForSeamType,
  type VanDerValkPitchedCladdingType,
} from './pitchedCladdingFieldConfig'
import { resolvePitchedRoofHookType, screwTypeRequiredForRoofHook, isValidSolarFixRoofConstructionHeight } from './pitchedBomFrameProfiles'
import {
  isMaxCtcDistanceRange,
  isScalarWithinMaxCtcDistanceRange,
  maxCtcDistanceOptionsFromRange,
  parsePositiveNumberMm,
} from './solarFixFieldUtils'
import type { VanDerValkSuitabilityConfig } from './types'
import { getVanDerValkModuleGridsForBom } from './vanDerValkModuleGrids'
import { groupSuitabilityRowsByBaseLabel, suitabilityGroupLabel } from './vanDerValkSuitabilityLabels'

/** Field order for cascading dropdowns (coarse → fine). Excludes vendor fields driven only via panel sync (tilt, gap, paths, etc.). */
export const VANDERVALK_CASCADE_FIELD_ORDER: (keyof VanDerValkSuitabilityConfig)[] = [
  'name',
  'system',
  'placeSystem',
  'moduleOrientation',
  'rowPitch',
  'vandervalkExecution',
]

/**
 * Fields (after the shared base label) used to disambiguate duplicate "configuration" options.
 * Order is coarse → fine for cascading: each dropdown only shows values still available given prior picks.
 * Omit tilt, gap, dilatation*, frontPath, backPath, pvModuleLift — those apply from the row automatically.
 */
export const VANDERVALK_VARIANT_FIELD_ORDER: (keyof VanDerValkSuitabilityConfig)[] = [
  'rowPitch',
  'vandervalkExecution',
]

/** Variant field order for pickers only (`vandervalkExecution` is still on the row; not shown in UI). */
export const VANDERVALK_VARIANT_PICKER_FIELD_ORDER: (keyof VanDerValkSuitabilityConfig)[] =
  VANDERVALK_VARIANT_FIELD_ORDER.filter((f) => f !== 'vandervalkExecution')

/**
 * Pitched-roof (on-roof) cascade order. Kept separate from the flat-roof order so flat-roof pickers
 * are unaffected (the picker renders every field in its order, not only discriminating ones).
 */
export const VANDERVALK_PITCHED_VARIANT_FIELD_ORDER: (keyof VanDerValkSuitabilityConfig)[] = [
  'moduleOrientation',
  'color',
  'fixation',
  'longProfileType',
  'longProfile',
  'profileLength',
  'anchorType',
  'fastenerType',
  'roofMaterial',
  'maxCtcDistance',
  'roofConstructionHeight',
  'roofHookType',
  'screwType',
  'hangerBolt',
  'seamClampType',
  'mountingBracketBoltSize',
]

/** Pitched picker order (all pitched variant fields are user-facing). */
export const VANDERVALK_PITCHED_VARIANT_PICKER_FIELD_ORDER: (keyof VanDerValkSuitabilityConfig)[] =
  VANDERVALK_PITCHED_VARIANT_FIELD_ORDER

/** Bump when pitched suitability row flattening changes (forces one suitability re-fetch). */
export const VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION = 8

/** Vendor compatibility table for ValkSolarFix roof construction height (mm). */
export const SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS: Record<string, Record<string, number[]>> = {
  WOODEN_ROOF_FLOOR: { ROOF_SCREWS: [15, 80, 95, 100, 195, 235, 285, 355] },
  METAL_ROOF_FLOOR: { ROOF_SCREWS: [30, 60, 80, 110, 210, 250, 300], TOGGLE_ANCHOR: [270, 370] },
  MASSIVE_CONCRETE_FLOOR: { CONCRETE_ANCHOR: [225, 325] },
  HOLLOW_CORE_SLAB_FLOOR: { CONCRETE_ANCHOR: [225, 325] },
  AERATED_CONCRETE_ROOF_FLOOR: { ROOF_SCREWS: [25, 45, 65, 95, 125, 175] },
}

export function groupUsesPitchedSolarFix(rows: VanDerValkSuitabilityConfig[]): boolean {
  if (rows.length === 0) {
    return false
  }
  return rows.every(
    (r) => r.fixation === 'VALK_SOLAR_FIX' || r.system === 'VALK_PITCHED_VALK_SOLAR_FIX'
  )
}

export function isPitchedSolarFixSelection(
  selections: Partial<Record<string, string>>,
  groupRows: VanDerValkSuitabilityConfig[] = []
): boolean {
  if (selections.fixation === 'VALK_SOLAR_FIX') {
    return true
  }
  return groupUsesPitchedSolarFix(groupRows)
}

/** Pitched variant fields visible for the current fixation / anchor path. */
export function getPitchedVariantFieldsForContext(
  groupRows: VanDerValkSuitabilityConfig[],
  selections: Partial<Record<string, string>>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): (keyof VanDerValkSuitabilityConfig)[] {
  const hidden = new Set<keyof VanDerValkSuitabilityConfig>()
  const isSolarFix = isPitchedSolarFixSelection(selections, groupRows)

  if (isSolarFix) {
    hidden.add('roofHookType')
    hidden.add('screwType')
    hidden.add('longProfile')
    if (selections.anchorType === 'OTHER') {
      hidden.add('fastenerType')
      hidden.add('roofMaterial')
      hidden.add('roofConstructionHeight')
    }
  } else {
    hidden.add('maxCtcDistance')
    hidden.add('anchorType')
    hidden.add('fastenerType')
    hidden.add('roofMaterial')
    hidden.add('roofConstructionHeight')
  }

  return fieldOrder.filter((f) => !hidden.has(f))
}

/** Cascade field order for a system: pitched (on-roof) uses its own order; everything else uses flat-roof. */
export function getVanDerValkVariantFieldOrder(
  mountingSystemType?: string | null
): (keyof VanDerValkSuitabilityConfig)[] {
  return mountingSystemType === 'onRoof' ? VANDERVALK_PITCHED_VARIANT_FIELD_ORDER : VANDERVALK_VARIANT_FIELD_ORDER
}

/** Picker (UI) field order for a system: pitched (on-roof) uses its own order; everything else uses flat-roof. */
export function getVanDerValkVariantPickerFieldOrder(
  mountingSystemType?: string | null
): (keyof VanDerValkSuitabilityConfig)[] {
  return mountingSystemType === 'onRoof'
    ? VANDERVALK_PITCHED_VARIANT_PICKER_FIELD_ORDER
    : VANDERVALK_VARIANT_PICKER_FIELD_ORDER
}

export function serializeSuitabilityFieldValue(v: unknown): string {
  if (v === undefined || v === null) return ''
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v)
    } catch {
      return String(v)
    }
  }
  return String(v)
}

export function getUniqueByField(
  rows: VanDerValkSuitabilityConfig[],
  field: keyof VanDerValkSuitabilityConfig
): string[] {
  const set = new Set<string>()
  for (const row of rows) {
    set.add(serializeSuitabilityFieldValue(row[field]))
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** User-selectable options only — omits null/undefined (serialized as ``). */
export function getMeaningfulUniqueByField(
  rows: VanDerValkSuitabilityConfig[],
  field: keyof VanDerValkSuitabilityConfig
): string[] {
  return getUniqueByField(rows, field).filter((opt) => opt !== '')
}

/** User picks stored on the row at BOM time, not always present on flattened suitability rows. */
const SYNTHETIC_VARIANT_MATCH_FIELDS = new Set<keyof VanDerValkSuitabilityConfig>([
  'screwType',
  'seamClampType',
  'maxCtcDistance',
  'roofConstructionHeight',
])

export function filterBySelections(
  rows: VanDerValkSuitabilityConfig[],
  selections: Partial<Record<string, string>>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_CASCADE_FIELD_ORDER
): VanDerValkSuitabilityConfig[] {
  return rows.filter((row) => {
    for (const f of fieldOrder) {
      const need = selections[f as string]
      if (need === undefined) continue

      if (f === 'maxCtcDistance') {
        const rowVal = row.maxCtcDistance
        if (isMaxCtcDistanceRange(rowVal)) {
          const needScalar = parsePositiveNumberMm(need)
          if (needScalar == null || !isScalarWithinMaxCtcDistanceRange(needScalar, rowVal)) {
            return false
          }
          continue
        }
        const rowScalar = parsePositiveNumberMm(rowVal)
        if (rowScalar == null) {
          continue
        }
        if (String(rowScalar) !== need) {
          return false
        }
        continue
      }

      if (f === 'roofConstructionHeight') {
        const rowScalar = parsePositiveNumberMm(row.roofConstructionHeight)
        if (rowScalar == null) {
          continue
        }
        if (String(rowScalar) !== need) {
          return false
        }
        continue
      }

      const rowVal = serializeSuitabilityFieldValue(row[f])
      if (rowVal === '' && SYNTHETIC_VARIANT_MATCH_FIELDS.has(f)) {
        continue
      }
      if (rowVal !== need) {
        return false
      }
    }
    return true
  })
}

const ROW_COMPARE_KEYS: (keyof VanDerValkSuitabilityConfig)[] = [
  'name',
  'system',
  'vandervalkExecution',
  'placeSystem',
  'moduleOrientation',
  'rowPitch',
  'tilt',
  'gap',
  'dilatationMaxColumns',
  'dilatationMaxRows',
  'dilatationMaxColumnsLength',
  'dilatationMaxRowsLength',
  'frontPath',
  'backPath',
  'pvModuleLift',
  'color',
  'fixation',
  'longProfileType',
  'profileLength',
  'hangerBolt',
  'roofHookType',
  'longProfile',
  'screwType',
  'seamClampType',
  'mountingBracketBoltSize',
  'maxCtcDistance',
  'anchorType',
  'fastenerType',
  'roofMaterial',
  'roofConstructionHeight',
  'restrictPlacementToSubstructure',
]

function normalizeForCompare(v: unknown): string {
  if (v === undefined) return ''
  if (v === null) return ''
  if (Array.isArray(v) || (typeof v === 'object' && v !== null)) {
    try {
      return JSON.stringify(v)
    } catch {
      return String(v)
    }
  }
  return String(v)
}

function rowIdentityFieldMatches(
  key: keyof VanDerValkSuitabilityConfig,
  a: VanDerValkSuitabilityConfig,
  b: VanDerValkSuitabilityConfig
): boolean {
  const aVal = normalizeForCompare(a[key])
  const bVal = normalizeForCompare(b[key])
  if (aVal === bVal) {
    return true
  }
  if (SYNTHETIC_VARIANT_MATCH_FIELDS.has(key)) {
    return aVal === '' || bVal === ''
  }
  return false
}

export function isSameSuitabilityRow(
  a: VanDerValkSuitabilityConfig,
  b: VanDerValkSuitabilityConfig
): boolean {
  for (const k of ROW_COMPARE_KEYS) {
    if (!rowIdentityFieldMatches(k, a, b)) {
      return false
    }
  }
  return true
}

/** Full persisted selection equality, including synthetic user picks. */
export function isSamePersistedSuitabilitySelection(
  a: VanDerValkSuitabilityConfig,
  b: VanDerValkSuitabilityConfig
): boolean {
  if (!isSameSuitabilityRow(a, b)) {
    return false
  }
  for (const k of SYNTHETIC_VARIANT_MATCH_FIELDS) {
    if (normalizeForCompare(a[k]) !== normalizeForCompare(b[k])) {
      return false
    }
  }
  return true
}

export function rowInList(
  row: VanDerValkSuitabilityConfig,
  list: VanDerValkSuitabilityConfig[] | null | undefined
): boolean {
  if (!list?.length) return false
  return list.some((r) => isSameSuitabilityRow(r, row))
}
export function deriveSelectionsFromRow(
  row: VanDerValkSuitabilityConfig,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_CASCADE_FIELD_ORDER
): Record<string, string> {
  const s: Record<string, string> = {}
  for (const f of fieldOrder) {
    if (f === 'maxCtcDistance' || f === 'roofConstructionHeight') {
      const scalar = parsePositiveNumberMm(row[f])
      if (scalar != null) {
        s[f] = String(scalar)
      }
      continue
    }
    s[f] = serializeSuitabilityFieldValue(row[f])
  }
  return s
}

export function deriveVariantSelectionsFromRow(
  row: VanDerValkSuitabilityConfig,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_VARIANT_FIELD_ORDER
): Record<string, string> {
  return deriveSelectionsFromRow(row, fieldOrder)
}

const SOLAR_FIX_PERSISTED_CASCADE_FIELDS: (keyof VanDerValkSuitabilityConfig)[] = [
  'anchorType',
  'fastenerType',
  'roofMaterial',
]

function isSolarFixSuitabilityRow(row: VanDerValkSuitabilityConfig): boolean {
  return row.fixation === 'VALK_SOLAR_FIX' || row.system === 'VALK_PITCHED_VALK_SOLAR_FIX'
}

/** Vendor row selections plus synthetic picks (``screwType``, ``seamClampType``) from persisted selection. */
export function deriveVariantSelectionsFromPersistedRow(
  vendorRow: VanDerValkSuitabilityConfig,
  persisted: VanDerValkSuitabilityConfig | null | undefined,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_VARIANT_FIELD_ORDER
): Record<string, string> {
  const base = deriveVariantSelectionsFromRow(vendorRow, fieldOrder)
  if (!persisted) {
    return base
  }
  if (!isSameSuitabilityRow(vendorRow, persisted)) {
    if (isSolarFixSuitabilityRow(persisted)) {
      return deriveVariantSelectionsFromRow(persisted, fieldOrder)
    }
    const merged = { ...base, ...deriveVariantSelectionsFromRow(persisted, fieldOrder) }
    if (fieldOrder.includes('moduleOrientation')) {
      const persistedOrientation = serializeSuitabilityFieldValue(persisted.moduleOrientation)
      if (persistedOrientation !== '') {
        merged.moduleOrientation = persistedOrientation
      }
    }
    return merged
  }
  if (fieldOrder.includes('moduleOrientation')) {
    const persistedOrientation = serializeSuitabilityFieldValue(persisted.moduleOrientation)
    if (persistedOrientation !== '') {
      base.moduleOrientation = persistedOrientation
    }
  }
  for (const f of SYNTHETIC_VARIANT_MATCH_FIELDS) {
    if (f === 'maxCtcDistance' || f === 'roofConstructionHeight') {
      const scalar = parsePositiveNumberMm(persisted[f])
      if (scalar != null) {
        base[f as string] = String(scalar)
      }
      continue
    }
    const val = serializeSuitabilityFieldValue(persisted[f])
    if (val !== '') {
      base[f as string] = val
    }
  }
  if (isSolarFixSuitabilityRow(persisted)) {
    for (const f of SOLAR_FIX_PERSISTED_CASCADE_FIELDS) {
      const val = serializeSuitabilityFieldValue(persisted[f])
      if (val === '') {
        delete base[f as string]
      } else {
        base[f as string] = val
      }
    }
  }
  return base
}

/**
 * Keys that differ somewhere in the group. Used to know which properties can in principle
 * be chosen (cascade order still applies per-field options).
 */
export function getDiscriminatingVariantFieldKeys(
  groupRows: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_VARIANT_FIELD_ORDER
): (keyof VanDerValkSuitabilityConfig)[] {
  return fieldOrder.filter((f) => getUniqueByField(groupRows, f).length > 1)
}

/**
 * Cascading options for a field: unique values in rows that match all **earlier** discriminating
 * picks (VANDERVALK_VARIANT_FIELD_ORDER), so e.g. after row pitch is set, later fields may narrow
 * to a single value (caller hides the dropdown in that case).
 */
export function getCascadingVariantOptionsForField(
  groupRows: VanDerValkSuitabilityConfig[],
  field: keyof VanDerValkSuitabilityConfig,
  discriminatingKeys: (keyof VanDerValkSuitabilityConfig)[],
  variantSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_VARIANT_FIELD_ORDER
): string[] {
  void discriminatingKeys
  const effective = getEffectiveSelections(groupRows, variantSelections, fieldOrder)
  const prior: Record<string, string> = {}
  for (const f of fieldOrder) {
    if (f === field) {
      break
    }
    const v = effective[f as string]
    if (v !== undefined && v !== '') {
      prior[f as string] = v
    }
  }
  const pool = filterBySelections(groupRows, prior, fieldOrder) as VanDerValkSuitabilityConfig[]
  return getMeaningfulUniqueByField(pool, field)
}

/** True when the selected fixation path requires a vendor ``screwType`` on the BOM frame. */
export function pitchedRoofHookFixationNeedsScrewType(
  fixation: string | undefined | null,
  roofHookType: string | undefined | null,
  claddingType?: VanDerValkPitchedCladdingType | null
): boolean {
  if (fixation !== 'ROOF_HOOK') {
    return false
  }
  const hook = resolvePitchedRoofHookType(claddingType ?? null, roofHookType)
  return screwTypeRequiredForRoofHook(hook)
}

function screwTypesFromRowMetadata(row: VanDerValkSuitabilityConfig): string[] {
  const meta = (row as VanDerValkSuitabilityConfig & { availableScrewTypes?: unknown }).availableScrewTypes
  if (!Array.isArray(meta)) {
    return []
  }
  return meta.map(String).filter((s) => s.trim() !== '')
}

function collectScrewOptionsFromRows(rows: VanDerValkSuitabilityConfig[]): string[] {
  const screws = new Set<string>()
  for (const row of rows) {
    if (nonEmptySuitabilityField(row.screwType)) {
      screws.add(String(row.screwType))
    }
    for (const screw of screwTypesFromRowMetadata(row)) {
      screws.add(screw)
    }
  }
  return Array.from(screws).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

function collectSeamClampOptionsFromRows(rows: VanDerValkSuitabilityConfig[]): string[] {
  const clamps = new Set<string>()
  for (const row of rows) {
    if (nonEmptySuitabilityField(row.seamClampType)) {
      clamps.add(String(row.seamClampType))
    }
  }
  return Array.from(clamps).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** Seam-clamp picker options; falls back to cladding ``seamType`` when rows omit ``seamClampType``. */
export function getPitchedSeamClampTypePickerOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  variantSelections: Record<string, string>,
  seamType: string | null | undefined,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): string[] {
  const fromRows = getCascadingVariantOptionsForField(
    groupRows,
    'seamClampType',
    getDiscriminatingVariantFieldKeys(groupRows, fieldOrder),
    variantSelections,
    fieldOrder
  )
  if (fromRows.length > 0) {
    return fromRows
  }

  const effective = getEffectiveSelections(groupRows, variantSelections, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder) as VanDerValkSuitabilityConfig[]
  const fromPool = collectSeamClampOptionsFromRows(pool)
  if (fromPool.length > 0) {
    return fromPool
  }

  return getSeamClampTypesForSeamType(seamType)
}

/** Screw-type picker options after profile/fixation cascade; falls back to row ``availableScrewTypes``. */
export function getPitchedScrewTypePickerOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  variantSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): string[] {
  const fromRows = getCascadingVariantOptionsForField(
    groupRows,
    'screwType',
    getDiscriminatingVariantFieldKeys(groupRows, fieldOrder),
    variantSelections,
    fieldOrder
  )
  if (fromRows.length > 0) {
    return fromRows
  }

  const effective = getEffectiveSelections(groupRows, variantSelections, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder) as VanDerValkSuitabilityConfig[]
  const fromPool = collectScrewOptionsFromRows(pool)
  if (fromPool.length > 0) {
    return fromPool
  }

  const prior: Record<string, string> = {}
  for (const f of fieldOrder) {
    if (f === 'screwType') {
      break
    }
    const v = effective[f as string] ?? variantSelections[f as string]
    if (v !== undefined && v !== '') {
      prior[f as string] = v
    }
  }
  const matchingRows = filterBySelections(groupRows, prior, fieldOrder) as VanDerValkSuitabilityConfig[]
  return collectScrewOptionsFromRows(matchingRows)
}

/** Roof construction height options for ValkSolarFix; vendor pool rows first, then compatibility table. */
export function getPitchedSolarFixRoofConstructionHeightOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  variantSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER,
  selectedRow?: VanDerValkSuitabilityConfig | null
): string[] {
  const roofMaterial =
    variantSelections.roofMaterial ??
    (selectedRow?.roofMaterial != null ? String(selectedRow.roofMaterial) : undefined)
  const fastenerType =
    variantSelections.fastenerType ??
    (selectedRow?.fastenerType != null ? String(selectedRow.fastenerType) : undefined)
  if (!roofMaterial || !fastenerType) {
    return []
  }

  const effective = getEffectiveSelections(groupRows, variantSelections, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder)
  const fromPool = new Set<string>()
  for (const row of pool) {
    if (String(row.fastenerType ?? '') !== fastenerType || String(row.roofMaterial ?? '') !== roofMaterial) {
      continue
    }
    const height = parsePositiveNumberMm(row.roofConstructionHeight)
    if (height != null) {
      fromPool.add(String(height))
    }
  }
  if (fromPool.size > 0) {
    return Array.from(fromPool).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }

  const heights = SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS[roofMaterial]?.[fastenerType] ?? []
  return heights.map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** True when the fastener + roof material pair has at least one roof construction height option. */
export function pitchedSolarFixMaterialHasHeightOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  variantSelections: Record<string, string>,
  roofMaterial: string,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER,
  selectedRow?: VanDerValkSuitabilityConfig | null
): boolean {
  return (
    getPitchedSolarFixRoofConstructionHeightOptions(
      groupRows,
      { ...variantSelections, roofMaterial },
      fieldOrder,
      selectedRow
    ).length > 0
  )
}

/** Roof material options for ValkSolarFix; omits materials with no height options when anchor requires heights. */
export function getPitchedSolarFixRoofMaterialOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  discriminatingKeys: (keyof VanDerValkSuitabilityConfig)[],
  variantSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER,
  selectedRow?: VanDerValkSuitabilityConfig | null
): string[] {
  const base = getCascadingVariantOptionsForField(
    groupRows,
    'roofMaterial',
    discriminatingKeys,
    variantSelections,
    fieldOrder
  )
  if (variantSelections.anchorType !== 'VALK_SOLAR_FIX') {
    return base
  }
  return base.filter((material) =>
    pitchedSolarFixMaterialHasHeightOptions(groupRows, variantSelections, material, fieldOrder, selectedRow)
  )
}

/** Fastener options for ValkSolarFix; omits fasteners with no material+height path when anchor requires heights. */
export function getPitchedSolarFixFastenerTypeOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  discriminatingKeys: (keyof VanDerValkSuitabilityConfig)[],
  variantSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER,
  selectedRow?: VanDerValkSuitabilityConfig | null
): string[] {
  const base = getCascadingVariantOptionsForField(
    groupRows,
    'fastenerType',
    discriminatingKeys,
    variantSelections,
    fieldOrder
  )
  if (variantSelections.anchorType !== 'VALK_SOLAR_FIX') {
    return base
  }
  return base.filter((fastenerType) => {
    const materials = getCascadingVariantOptionsForField(
      groupRows,
      'roofMaterial',
      discriminatingKeys,
      { ...variantSelections, fastenerType },
      fieldOrder
    )
    return materials.some((material) =>
      pitchedSolarFixMaterialHasHeightOptions(
        groupRows,
        { ...variantSelections, fastenerType },
        material,
        fieldOrder,
        selectedRow
      )
    )
  })
}

export type StripInvalidSolarFixFieldsOpts = {
  groupRows?: VanDerValkSuitabilityConfig[]
  fieldOrder?: (keyof VanDerValkSuitabilityConfig)[]
}

/** Strip invalid solar-fix cascade + synthetic picks (height, max CTC, roof material). */
export function stripInvalidSolarFixSyntheticFields(
  row: VanDerValkSuitabilityConfig,
  opts?: StripInvalidSolarFixFieldsOpts
): VanDerValkSuitabilityConfig {
  if (row.anchorType !== 'VALK_SOLAR_FIX') {
    return row
  }
  const fieldOrder = opts?.fieldOrder ?? VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
  const fastenerType = row.fastenerType != null ? String(row.fastenerType) : ''
  let roofMaterial = row.roofMaterial != null ? String(row.roofMaterial) : ''
  let next = row

  if (roofMaterial !== '' && opts?.groupRows?.length) {
    const disc = getDiscriminatingVariantFieldKeys(opts.groupRows, fieldOrder)
    const variantSelections = deriveVariantSelectionsFromRow(next, fieldOrder)
    const validMaterials = getPitchedSolarFixRoofMaterialOptions(
      opts.groupRows,
      disc,
      variantSelections,
      fieldOrder,
      next
    )
    if (validMaterials.length > 0 && !validMaterials.includes(roofMaterial)) {
      next = { ...next, roofMaterial: undefined, roofConstructionHeight: undefined }
      roofMaterial = ''
    }
  }

  const height = parsePositiveNumberMm(next.roofConstructionHeight)
  if (height != null) {
    const heightValid =
      fastenerType !== '' &&
      roofMaterial !== '' &&
      isValidSolarFixRoofConstructionHeight(roofMaterial, fastenerType, height)
    if (!heightValid) {
      next = { ...next, roofConstructionHeight: undefined }
    }
  }
  const maxScalar = parsePositiveNumberMm(next.maxCtcDistance)
  if (maxScalar != null && isMaxCtcDistanceRange(row.maxCtcDistance)) {
    if (!isScalarWithinMaxCtcDistanceRange(maxScalar, row.maxCtcDistance)) {
      next = { ...next, maxCtcDistance: row.maxCtcDistance }
    }
  }
  return next
}

/** Apply solar-fix persistence rules: honour cleared cascade picks and strip invalid material/height. */
export function finalizeSolarFixRowForPersistence(
  resolved: VanDerValkSuitabilityConfig,
  effective: Record<string, string>,
  groupRows: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig {
  if (resolved.anchorType !== 'VALK_SOLAR_FIX') {
    return resolved
  }
  let row = resolved
  if (!effective.roofMaterial) {
    row = { ...row, roofMaterial: undefined, roofConstructionHeight: undefined }
  }
  return stripInvalidSolarFixSyntheticFields(row, { groupRows, fieldOrder })
}

/**
 * Apply solar-fix picker display defaults onto a row (matches what dropdowns show as ``options[0]``).
 * Used at BOM time and to persist integration_json when cascade fields look complete in the UI.
 */
export function applySolarFixPickerDisplayDefaultsToRow(
  row: VanDerValkSuitabilityConfig,
  resultArray: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig {
  if (row.fixation !== 'VALK_SOLAR_FIX' && row.system !== 'VALK_PITCHED_VALK_SOLAR_FIX') {
    return row
  }

  const { groups } = groupSuitabilityRowsByBaseLabel(resultArray)
  const groupRows = groups.get(suitabilityGroupLabel(row))
  if (!groupRows?.length) {
    return row
  }

  let next = { ...row }
  const userSelections = deriveVariantSelectionsFromRow(next, fieldOrder)
  const effective = getEffectiveSelections(groupRows, userSelections, fieldOrder)
  const disc = getDiscriminatingVariantFieldKeys(groupRows, fieldOrder)

  for (const f of ['anchorType', 'fastenerType', 'roofMaterial'] as const) {
    if (nonEmptySuitabilityField(next[f])) {
      continue
    }
    const effVal = effective[f]
    if (effVal) {
      next = { ...next, [f]: effVal }
    }
  }

  if (next.anchorType === 'VALK_SOLAR_FIX' && !nonEmptySuitabilityField(next.roofMaterial)) {
    const materialOpts = getPitchedSolarFixRoofMaterialOptions(
      groupRows,
      disc,
      { ...effective, ...deriveVariantSelectionsFromRow(next, fieldOrder) },
      fieldOrder,
      next
    )
    if (materialOpts.length > 0) {
      next = { ...next, roofMaterial: materialOpts[0] }
    }
  }

  const partialAfter = deriveVariantSelectionsFromRow(next, fieldOrder)

  if (!parsePositiveNumberMm(next.maxCtcDistance) || isMaxCtcDistanceRange(next.maxCtcDistance)) {
    const maxOpts = getPitchedSolarFixMaxCtcDistanceOptions(groupRows, partialAfter, fieldOrder)
    const defaultMax = syntheticNumericFieldValueForPicker('maxCtcDistance', next.maxCtcDistance, maxOpts)
    if (defaultMax !== '') {
      next = { ...next, maxCtcDistance: Number(defaultMax) }
    }
  }

  if (next.anchorType === 'VALK_SOLAR_FIX' && !parsePositiveNumberMm(next.roofConstructionHeight)) {
    const heightOpts = getPitchedSolarFixRoofConstructionHeightOptions(groupRows, partialAfter, fieldOrder, next)
    const defaultHeight = syntheticNumericFieldValueForPicker(
      'roofConstructionHeight',
      next.roofConstructionHeight,
      heightOpts
    )
    if (defaultHeight !== '') {
      next = { ...next, roofConstructionHeight: Number(defaultHeight) }
    }
  }

  return stripInvalidSolarFixSyntheticFields(next, { groupRows, fieldOrder })
}

/** True when changing this field invalidates downstream solar-fix synthetic picks. */
export function solarFixCascadeFieldInvalidatesSyntheticFields(
  field: keyof VanDerValkSuitabilityConfig
): boolean {
  return field === 'anchorType' || field === 'fastenerType' || field === 'roofMaterial'
}

/** Missing synthetic solar-fix fields required for MEMBRANE BOM when anchor is VALK_SOLAR_FIX. */
export function getPitchedSolarFixMissingBomFields(row: VanDerValkSuitabilityConfig): (keyof VanDerValkSuitabilityConfig)[] {
  const missing: (keyof VanDerValkSuitabilityConfig)[] = []
  if (parsePositiveNumberMm(row.maxCtcDistance) == null && !isMaxCtcDistanceRange(row.maxCtcDistance)) {
    missing.push('maxCtcDistance')
  }
  if (row.anchorType === 'VALK_SOLAR_FIX' && parsePositiveNumberMm(row.roofConstructionHeight) == null) {
    missing.push('roofConstructionHeight')
  }
  return missing
}

/** Max CTC distance options for ValkSolarFix: scalar picks within vendor ``maxCtcDistance`` range on pool rows. */
export function getPitchedSolarFixMaxCtcDistanceOptions(
  groupRows: VanDerValkSuitabilityConfig[],
  variantSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): string[] {
  if (!isPitchedSolarFixSelection(variantSelections, groupRows)) {
    return []
  }
  const effective = getEffectiveSelections(groupRows, variantSelections, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder)
  const opts = new Set<string>()
  for (const row of pool) {
    const v = row.maxCtcDistance
    if (isMaxCtcDistanceRange(v)) {
      for (const opt of maxCtcDistanceOptionsFromRange(v)) {
        opts.add(opt)
      }
    } else {
      const scalar = parsePositiveNumberMm(v)
      if (scalar != null) {
        opts.add(String(scalar))
      }
    }
  }
  return Array.from(opts).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** Scalar picker value for synthetic numeric fields (ignores vendor range JSON on the row). */
export function syntheticNumericFieldValueForPicker(
  field: keyof VanDerValkSuitabilityConfig,
  raw: unknown,
  options: string[]
): string {
  const scalar = parsePositiveNumberMm(raw)
  if (scalar != null) {
    const asString = String(scalar)
    if (options.includes(asString)) {
      return asString
    }
  }
  return options[0] ?? ''
}

export function solarFixSyntheticFieldsComplete(row: VanDerValkSuitabilityConfig): boolean {
  const maxScalar = parsePositiveNumberMm(row.maxCtcDistance)
  if (maxScalar == null) {
    return false
  }
  if (row.anchorType === 'OTHER') {
    return true
  }
  if (row.anchorType !== 'VALK_SOLAR_FIX') {
    return false
  }
  return parsePositiveNumberMm(row.roofConstructionHeight) != null
}

/**
 * Merges user picks with auto-selection where only one value remains for a field
 * (given prior fields in `fieldOrder`).
 */
export function getEffectiveSelections(
  allRows: VanDerValkSuitabilityConfig[],
  userSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_CASCADE_FIELD_ORDER
): Record<string, string> {
  const s: Record<string, string> = {}
  for (const f of fieldOrder) {
    if (SYNTHETIC_VARIANT_MATCH_FIELDS.has(f)) {
      if (userSelections[f] != null && userSelections[f] !== '') {
        s[f] = userSelections[f]
      }
      continue
    }
    if (userSelections[f] != null) {
      s[f] = userSelections[f]
      continue
    }
    const prior: Record<string, string> = { ...s }
    const rel = filterBySelections(allRows, prior, fieldOrder)
    const opts = getUniqueByField(rel, f)
    if (opts.length === 1 && opts[0] !== '') {
      s[f] = opts[0]
    }
  }
  return s
}

/**
 * Build a partial persisted suitability row from cascade picks when the pool still has
 * multiple vendor rows (e.g. orientation chosen but color still ambiguous).
 */
export function mergeVanDerValkSuitabilityRowWithVariantSelections(
  baseRow: VanDerValkSuitabilityConfig,
  pool: VanDerValkSuitabilityConfig[],
  effective: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig {
  let row: VanDerValkSuitabilityConfig = { ...baseRow }
  for (const f of fieldOrder) {
    const need = effective[f as string]
    if (need === undefined || need === '') {
      continue
    }
    const donor = pool.find((r) => serializeSuitabilityFieldValue(r[f]) === need)
    if (donor) {
      row = { ...row, [f]: donor[f] }
    } else if (SYNTHETIC_VARIANT_MATCH_FIELDS.has(f)) {
      if (f === 'maxCtcDistance' || f === 'roofConstructionHeight') {
        const n = parsePositiveNumberMm(need)
        if (n != null) {
          row = { ...row, [f]: n }
        }
      } else {
        row = { ...row, [f]: need }
      }
    }
  }
  return row
}

function vendorOrientationMatchesStudioLayout(vendorSerialized: string, layout: ModuleLayout): boolean {
  const s0 = String(vendorSerialized).trim().toLowerCase()
  if (!s0) {
    return false
  }
  const compact = s0.replace(/[_\s-]+/g, '')
  if (layout === 'landscape') {
    return compact === 'l' || compact === 'landscape' || s0.includes('landscape')
  }
  if (layout === 'portrait') {
    return compact === 'p' || compact === 'portrait' || s0.includes('portrait')
  }
  return false
}

function moduleLayoutFromRowOrientation(row: VanDerValkSuitabilityConfig): ModuleLayout | null {
  const mo = serializeSuitabilityFieldValue(row.moduleOrientation)
  if (!mo) {
    return null
  }
  if (vendorOrientationMatchesStudioLayout(mo, 'portrait')) {
    return 'portrait'
  }
  if (vendorOrientationMatchesStudioLayout(mo, 'landscape')) {
    return 'landscape'
  }
  return null
}

/**
 * When both vendor orientations exist for the configuration group, keep studio panel layout
 * instead of applying a row that would flip orientation (e.g. auto-select used pool[0] before grids were active).
 * Single-orientation groups still snap via the unchanged row.
 */
export function resolveRowForPanelLayoutApply(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig
): VanDerValkSuitabilityConfig {
  const studioLayout = readPrimaryModuleLayoutFromSystem(system)
  const rowLayout = moduleLayoutFromRowOrientation(row)
  if (!studioLayout || !rowLayout || studioLayout === rowLayout) {
    return row
  }

  const rawList = (
    parseIntegrationJson(system.integration_json) as { vanDerValk?: { suitabilityResult?: unknown } } | null
  )?.vanDerValk?.suitabilityResult
  const resultArray = Array.isArray(rawList) ? (rawList as VanDerValkSuitabilityConfig[]) : []
  const groupKey = suitabilityGroupLabel(row)
  const groupRows = groupKey ? (groupSuitabilityRowsByBaseLabel(resultArray).groups.get(groupKey) ?? []) : []
  if (!groupRows.length) {
    return row
  }

  const orientationOpts = getUniqueByField(groupRows, 'moduleOrientation').filter((o) => o !== '')
  if (orientationOpts.length <= 1) {
    return row
  }

  const studioMatch = orientationOpts.find((o) => vendorOrientationMatchesStudioLayout(o, studioLayout))
  if (!studioMatch) {
    return row
  }

  const donor = groupRows.find((r) => serializeSuitabilityFieldValue(r.moduleOrientation) === studioMatch)
  if (!donor) {
    return row
  }

  return {
    ...row,
    moduleOrientation: donor.moduleOrientation,
    dilatationMaxColumns: donor.dilatationMaxColumns,
    dilatationMaxRows: donor.dilatationMaxRows,
    dilatationMaxColumnsLength: donor.dilatationMaxColumnsLength,
    dilatationMaxRowsLength: donor.dilatationMaxRowsLength,
  }
}

/** First active module grid's panel orientation (portrait / landscape). */
export function readPrimaryModuleLayoutFromSystem(
  system: StudioSystemType | null | undefined
): ModuleLayout | null {
  const grids = getVanDerValkModuleGridsForBom(system)
  if (!grids.length) {
    return null
  }
  const layout = grids[0].moduleLayout?.()
  if (layout === 'landscape' || layout === 'portrait') {
    return layout
  }
  return null
}

/**
 * Seed ``moduleOrientation`` from studio layout when both vendor options exist, or snap when only one.
 * Returns a partial selections map (may be empty when ambiguous and no studio layout).
 */
export function pickModuleOrientationForVariantSeed(
  system: StudioSystemType | null | undefined,
  groupRows: VanDerValkSuitabilityConfig[],
  priorSelections: Record<string, string>,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[]
): Record<string, string> {
  if (!groupRows.length || !fieldOrder.includes('moduleOrientation')) {
    return {}
  }
  const pool = filterBySelections(groupRows, priorSelections, fieldOrder) as VanDerValkSuitabilityConfig[]
  const opts = getUniqueByField(pool, 'moduleOrientation').filter((o) => o !== '')
  if (opts.length === 0) {
    return {}
  }
  if (opts.length === 1) {
    return { moduleOrientation: opts[0] }
  }
  const studioLayout = readPrimaryModuleLayoutFromSystem(system)
  if (studioLayout) {
    const match = opts.find((o) => vendorOrientationMatchesStudioLayout(o, studioLayout))
    if (match) {
      return { moduleOrientation: match }
    }
  }
  return {}
}

/** Initial variant picks when a suitable-system group is first chosen (studio-aware orientation). */
/** Vendor roof-hook types allowed for TILED roofs (product labels: Strongline, Strongline Heavy Duty, Plain Tile, Slate Tile). */
export const VANDERVALK_TILED_ROOF_HOOK_TYPES = ['STRONG', 'STRONG_HEAVY', 'TILE', 'SLATE'] as const

export function filterTiledRoofHookTypeOptions(
  claddingType: VanDerValkPitchedCladdingType | null | undefined,
  options: string[]
): string[] {
  if (claddingType !== 'TILED') {
    return options
  }
  const allowed = new Set<string>(VANDERVALK_TILED_ROOF_HOOK_TYPES)
  const filtered = options.filter((opt) => allowed.has(opt))
  return filtered.length > 0 ? filtered : options
}

/** Preferred pitched-roof variant defaults when multiple vendor options exist. */
export const VANDERVALK_PITCHED_PREFERRED_VARIANT_DEFAULTS: Partial<
  Record<keyof VanDerValkSuitabilityConfig, string>
> = {
  color: 'BLACK',
  profileLength: '3520',
  roofHookType: 'STRONG',
  screwType: 'M5x58mm',
  seamClampType: 'ROUND_CLAMP',
}

/** Pick the preferred pitched variant option when multiple choices remain in the cascade. */
export function pickPreferredPitchedVariantOption(
  field: keyof VanDerValkSuitabilityConfig,
  options: string[]
): string {
  if (options.length === 0) {
    return ''
  }
  if (options.length === 1) {
    return options[0]
  }
  const preferred = VANDERVALK_PITCHED_PREFERRED_VARIANT_DEFAULTS[field]
  if (preferred && options.includes(preferred)) {
    return preferred
  }
  return options[0]
}

function seedPitchedPreferredVariantSelections(
  system: StudioSystemType | null | undefined,
  groupRows: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[],
  prior: Record<string, string>,
  pitchedCladdingType: VanDerValkPitchedCladdingType | null = null
): Record<string, string> {
  if (system?.mounting_type !== 'onRoof' || !groupRows.length) {
    return prior
  }
  let partial = { ...prior }
  const contextualOrder = getPitchedVariantFieldsForContext(groupRows, partial, fieldOrder)
  const disc = getDiscriminatingVariantFieldKeys(groupRows, contextualOrder)
  for (const field of contextualOrder) {
    if (!disc.includes(field)) {
      continue
    }
    const current = partial[field as string]
    if (current != null && current !== '') {
      continue
    }
    let opts = getCascadingVariantOptionsForField(groupRows, field, disc, partial, contextualOrder)
    if (field === 'roofHookType') {
      opts = filterTiledRoofHookTypeOptions(pitchedCladdingType, opts)
    }
    if (opts.length === 1) {
      partial = { ...partial, [field as string]: opts[0] }
      continue
    }
    if (opts.length > 1 && VANDERVALK_PITCHED_PREFERRED_VARIANT_DEFAULTS[field]) {
      const preferred = pickPreferredPitchedVariantOption(field, opts)
      if (preferred) {
        partial = { ...partial, [field as string]: preferred }
      }
    }
  }
  return partial
}

export function buildInitialVariantSelectionsFromSystem(
  system: StudioSystemType | null | undefined,
  groupRows: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[],
  pitchedCladdingType: VanDerValkPitchedCladdingType | null = null
): Record<string, string> {
  const orientation = pickModuleOrientationForVariantSeed(system, groupRows, {}, fieldOrder)
  return seedPitchedPreferredVariantSelections(system, groupRows, fieldOrder, orientation, pitchedCladdingType)
}

function preferScrewTypeFromOptions(screwOptions: string[]): string | undefined {
  if (screwOptions.length === 0) {
    return undefined
  }
  const preferred = VANDERVALK_PITCHED_PREFERRED_VARIANT_DEFAULTS.screwType
  if (preferred && screwOptions.includes(preferred)) {
    return preferred
  }
  if (screwOptions.length === 1) {
    return screwOptions[0]
  }
  return undefined
}

/** Preferred orientation option for dropdown display when value unset and multiple options exist. */
export function pickPreferredModuleOrientationOption(
  system: StudioSystemType | null | undefined,
  options: string[]
): string {
  if (options.length === 0) {
    return ''
  }
  if (options.length === 1) {
    return options[0]
  }
  const studioLayout = readPrimaryModuleLayoutFromSystem(system)
  if (studioLayout) {
    const match = options.find((o) => vendorOrientationMatchesStudioLayout(o, studioLayout))
    if (match) {
      return match
    }
  }
  return options[0]
}

/**
 * Resolve the first suitable-system row for a group, seeding orientation from studio layout
 * instead of defaulting to ``groupRows[0]`` (often landscape).
 */
export function resolveInitialSuitabilityRowFromGroup(
  system: StudioSystemType,
  groupRows: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[],
  pitchedCladdingType: VanDerValkPitchedCladdingType | null = null
): { seed: Record<string, string>; row: VanDerValkSuitabilityConfig } {
  const seed = buildInitialVariantSelectionsFromSystem(system, groupRows, fieldOrder, pitchedCladdingType)
  const effective = getEffectiveSelections(groupRows, seed, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder) as VanDerValkSuitabilityConfig[]
  const row =
    pool.length === 1
      ? pool[0]
      : mergeVanDerValkSuitabilityRowWithVariantSelections(pool[0] ?? groupRows[0], pool, effective, fieldOrder)
  return { seed, row }
}

/** Copy user-picked synthetic variant fields onto a vendor row (not returned in suitability flattening). */
export function mergeSyntheticVariantFieldsFromSelected(
  resolved: VanDerValkSuitabilityConfig,
  selected: VanDerValkSuitabilityConfig
): VanDerValkSuitabilityConfig {
  let row = { ...resolved }
  if (nonEmptySuitabilityField(selected.seamClampType) && !nonEmptySuitabilityField(resolved.seamClampType)) {
    row = { ...row, seamClampType: String(selected.seamClampType).trim() }
  }
  if (nonEmptySuitabilityField(selected.screwType) && !nonEmptySuitabilityField(resolved.screwType)) {
    row = { ...row, screwType: String(selected.screwType).trim() }
  }
  const maxScalar = parsePositiveNumberMm(selected.maxCtcDistance)
  if (maxScalar != null && (isMaxCtcDistanceRange(resolved.maxCtcDistance) || parsePositiveNumberMm(resolved.maxCtcDistance) == null)) {
    row = { ...row, maxCtcDistance: maxScalar }
  }
  const heightScalar = parsePositiveNumberMm(selected.roofConstructionHeight)
  if (heightScalar != null && parsePositiveNumberMm(resolved.roofConstructionHeight) == null) {
    row = { ...row, roofConstructionHeight: heightScalar }
  }
  return row
}

/**
 * Expand a partially persisted ``selectedSuitability`` to the fully resolved vendor row when
 * cascade auto-selection (single remaining option per field) uniquely identifies one row in the group.
 * Matches the picker ``getEffectiveSelections`` + ``filterBySelections`` logic used at BOM time.
 */
export function resolveVanDerValkSuitabilityRowForBom(
  selected: VanDerValkSuitabilityConfig,
  resultArray: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig {
  if (!resultArray.length || !rowInList(selected, resultArray)) {
    return selected
  }

  const { groups } = groupSuitabilityRowsByBaseLabel(resultArray)
  const groupRows = groups.get(suitabilityGroupLabel(selected))
  if (!groupRows?.length) {
    return selected
  }

  const partialSelections = deriveVariantSelectionsFromRow(selected, fieldOrder)
  const userSelections: Record<string, string> = {}
  for (const [key, value] of Object.entries(partialSelections)) {
    if (value !== '') {
      userSelections[key] = value
    }
  }

  const effective = getEffectiveSelections(groupRows, userSelections, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder) as VanDerValkSuitabilityConfig[]
  if (pool.length === 1) {
    let resolved = mergeSyntheticVariantFieldsFromSelected(pool[0], selected)
    if (selected.roofMaterial == null) {
      resolved = { ...resolved, roofMaterial: undefined, roofConstructionHeight: undefined }
    }
    return stripInvalidSolarFixSyntheticFields(resolved, { groupRows, fieldOrder })
  }
  return stripInvalidSolarFixSyntheticFields(mergeSyntheticVariantFieldsFromSelected(selected, selected), {
    groupRows,
    fieldOrder,
  })
}

/** Resolve cascade row and infer ``screwType`` when the vendor supplies a single option in the group pool. */
export function enrichRoofHookRowFromSuitabilityResult(
  selected: VanDerValkSuitabilityConfig,
  resultArray: VanDerValkSuitabilityConfig[],
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig {
  let row = resolveVanDerValkSuitabilityRowForBom(selected, resultArray, fieldOrder)
  if (nonEmptySuitabilityField(row.screwType)) {
    return row
  }

  const selfMeta = screwTypesFromRowMetadata(row)
  if (selfMeta.length === 1) {
    return { ...row, screwType: selfMeta[0] }
  }

  const { groups } = groupSuitabilityRowsByBaseLabel(resultArray)
  const groupRows = groups.get(suitabilityGroupLabel(row))
  if (!groupRows?.length) {
    return row
  }

  const partialSelections = deriveVariantSelectionsFromRow(row, fieldOrder)
  const userSelections: Record<string, string> = {}
  for (const [key, value] of Object.entries(partialSelections)) {
    if (value !== '') {
      userSelections[key] = value
    }
  }
  const effective = getEffectiveSelections(groupRows, userSelections, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder) as VanDerValkSuitabilityConfig[]
  const screwTypes = new Set(
    pool.map((r) => r.screwType).filter((v) => nonEmptySuitabilityField(v)).map(String)
  )
  if (screwTypes.size === 1) {
    row = { ...row, screwType: [...screwTypes][0] }
  } else if (screwTypes.size > 1) {
    const preferred = preferScrewTypeFromOptions([...screwTypes])
    if (preferred) {
      row = { ...row, screwType: preferred }
    }
  } else {
    const metaTypes = new Set<string>()
    for (const r of pool) {
      for (const screw of screwTypesFromRowMetadata(r)) {
        metaTypes.add(screw)
      }
    }
    if (metaTypes.size === 1) {
      row = { ...row, screwType: [...metaTypes][0] }
    }
  }
  return row
}

/** Infer ``seamClampType`` from cladding ``seamType`` or a single option in the suitability pool. */
export function enrichStandingSeamRowFromCladdingAndResult(
  selected: VanDerValkSuitabilityConfig,
  resultArray: VanDerValkSuitabilityConfig[],
  seamType: string | null | undefined,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig {
  let row = mergePreservedSeamClampType(
    resolveVanDerValkSuitabilityRowForBom(selected, resultArray, fieldOrder),
    selected
  )
  if (nonEmptySuitabilityField(row.seamClampType)) {
    return row
  }

  const seamTypeClamps = getSeamClampTypesForSeamType(seamType)
  if (seamTypeClamps.length === 1) {
    return { ...row, seamClampType: seamTypeClamps[0] }
  }

  const { groups } = groupSuitabilityRowsByBaseLabel(resultArray)
  const groupRows = groups.get(suitabilityGroupLabel(row))
  if (!groupRows?.length) {
    return row
  }

  const partialSelections = deriveVariantSelectionsFromRow(row, fieldOrder)
  const userSelections: Record<string, string> = {}
  for (const [key, value] of Object.entries(partialSelections)) {
    if (value !== '') {
      userSelections[key] = value
    }
  }
  const clampOptions = getPitchedSeamClampTypePickerOptions(groupRows, userSelections, seamType, fieldOrder)
  if (clampOptions.length === 1) {
    row = { ...row, seamClampType: clampOptions[0] }
  } else if (clampOptions.length > 1) {
    const preferred = pickPreferredPitchedVariantOption('seamClampType', clampOptions)
    if (preferred) {
      row = { ...row, seamClampType: preferred }
    }
  }
  return row
}

export function enrichPitchedBomRowFromSuitabilityResult(
  selected: VanDerValkSuitabilityConfig,
  resultArray: VanDerValkSuitabilityConfig[],
  opts?: {
    seamType?: string | null
    fieldOrder?: (keyof VanDerValkSuitabilityConfig)[]
  }
): VanDerValkSuitabilityConfig {
  const fieldOrder = opts?.fieldOrder ?? VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
  let row = enrichRoofHookRowFromSuitabilityResult(selected, resultArray, fieldOrder)
  if (row.fixation === 'STANDING_SEAM_CLAMP' || row.system === 'VALK_PITCHED_STANDING_SEAM') {
    row = enrichStandingSeamRowFromCladdingAndResult(row, resultArray, opts?.seamType, fieldOrder)
    row = mergePreservedSeamClampType(row, selected)
  }
  if (row.fixation === 'VALK_SOLAR_FIX' || row.system === 'VALK_PITCHED_VALK_SOLAR_FIX') {
    row = applySolarFixPickerDisplayDefaultsToRow(row, resultArray, fieldOrder)
    row = mergePreservedMaxCtcDistance(row, selected)
    row = mergePreservedRoofConstructionHeight(row, selected)
    const { groups } = groupSuitabilityRowsByBaseLabel(resultArray)
    const groupRows = groups.get(suitabilityGroupLabel(row))
    row = stripInvalidSolarFixSyntheticFields(row, { groupRows, fieldOrder })
  }
  return row
}

function rowHasScrewTypeOrPickerOptions(row: VanDerValkSuitabilityConfig): boolean {
  if (nonEmptySuitabilityField(row.screwType)) {
    return true
  }
  return screwTypesFromRowMetadata(row).length > 0
}

/** True when cached rows look like stale grouping (roof-hook rows missing screw data). */
export function pitchedRoofHookRowsMissingScrewType(rows: VanDerValkSuitabilityConfig[]): boolean {
  const roofHookRows = rows.filter(
    (r) =>
      r.fixation === 'ROOF_HOOK' &&
      (r.system === 'VALK_PITCHED' || r.system === 'VALK_PITCHED_VALK_ACE') &&
      (nonEmptySuitabilityField(r.profileLength) || nonEmptySuitabilityField(r.longProfileType))
  )
  if (roofHookRows.length === 0) {
    return false
  }
  const withScrewData = roofHookRows.filter(rowHasScrewTypeOrPickerOptions)
  if (withScrewData.length === 0) {
    return true
  }
  return withScrewData.length < roofHookRows.length
}

export type SyntheticVariantField = 'screwType' | 'seamClampType' | 'maxCtcDistance' | 'roofConstructionHeight'

export function applySyntheticVariantFieldToRow(
  row: VanDerValkSuitabilityConfig,
  field: SyntheticVariantField,
  value: string
): VanDerValkSuitabilityConfig {
  if (field === 'maxCtcDistance' || field === 'roofConstructionHeight') {
    const n = parsePositiveNumberMm(value)
    return { ...row, [field]: n ?? value }
  }
  return { ...row, [field]: value }
}

/** Resolve base row for a synthetic variant pick when rows omit that field. */
export function resolveRowForSyntheticVariantPick(
  groupRows: VanDerValkSuitabilityConfig[],
  selections: Record<string, string>,
  field: SyntheticVariantField,
  fieldOrder: (keyof VanDerValkSuitabilityConfig)[] = VANDERVALK_PITCHED_VARIANT_FIELD_ORDER
): VanDerValkSuitabilityConfig | null {
  const prior: Record<string, string> = { ...selections }
  delete prior[field]
  const effective = getEffectiveSelections(groupRows, prior, fieldOrder)
  const pool = filterBySelections(groupRows, effective, fieldOrder)
  return pool[0] ?? null
}

/** Keep an explicit screw pick when the resolved pool row only carries ``availableScrewTypes`` metadata. */
export function mergePreservedRoofHookScrewType(
  resolved: VanDerValkSuitabilityConfig,
  previous: VanDerValkSuitabilityConfig | null | undefined
): VanDerValkSuitabilityConfig {
  const preserved = previous?.screwType
  if (nonEmptySuitabilityField(preserved) && !nonEmptySuitabilityField(resolved.screwType)) {
    return { ...resolved, screwType: String(preserved).trim() }
  }
  return resolved
}

/** Keep an explicit seam-clamp pick when the resolved pool row omits ``seamClampType``. */
export function mergePreservedSeamClampType(
  resolved: VanDerValkSuitabilityConfig,
  previous: VanDerValkSuitabilityConfig | null | undefined
): VanDerValkSuitabilityConfig {
  const preserved = previous?.seamClampType
  if (nonEmptySuitabilityField(preserved) && !nonEmptySuitabilityField(resolved.seamClampType)) {
    return { ...resolved, seamClampType: String(preserved).trim() }
  }
  return resolved
}

/** Keep an explicit max CTC pick when the resolved pool row only carries a vendor range object. */
export function mergePreservedMaxCtcDistance(
  resolved: VanDerValkSuitabilityConfig,
  previous: VanDerValkSuitabilityConfig | null | undefined
): VanDerValkSuitabilityConfig {
  const preserved = previous?.maxCtcDistance
  const preservedScalar = parsePositiveNumberMm(preserved)
  if (preservedScalar != null && (isMaxCtcDistanceRange(resolved.maxCtcDistance) || !parsePositiveNumberMm(resolved.maxCtcDistance))) {
    return { ...resolved, maxCtcDistance: preservedScalar }
  }
  return resolved
}

/** Keep an explicit roof construction height when the resolved pool row omits it. */
export function mergePreservedRoofConstructionHeight(
  resolved: VanDerValkSuitabilityConfig,
  previous: VanDerValkSuitabilityConfig | null | undefined
): VanDerValkSuitabilityConfig {
  const preserved = previous?.roofConstructionHeight
  const preservedScalar = parsePositiveNumberMm(preserved)
  if (preservedScalar == null || parsePositiveNumberMm(resolved.roofConstructionHeight) != null) {
    return resolved
  }
  const fastenerType = resolved.fastenerType != null ? String(resolved.fastenerType) : ''
  const roofMaterial = resolved.roofMaterial != null ? String(resolved.roofMaterial) : ''
  if (
    fastenerType === '' ||
    roofMaterial === '' ||
    !isValidSolarFixRoofConstructionHeight(roofMaterial, fastenerType, preservedScalar)
  ) {
    return resolved
  }
  return { ...resolved, roofConstructionHeight: preservedScalar }
}

export function nonEmptySuitabilityField(value: unknown): boolean {
  return value != null && String(value).trim() !== ''
}

